import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  Tag,
  Plus,
  Edit2,
  Trash2,
  ToggleLeft,
  ToggleRight,
  Percent,
  DollarSign,
  Calendar,
  Users,
  Copy,
  Check,
  X,
  AlertCircle,
  Search,
  Filter
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { Switch } from "../../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../../components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../../App";
import { useFeatureGate } from "../../hooks/useFeatureGate";
import FeatureGate from "../../components/FeatureGate";

const PromoCodesPage = () => {
  const [promoCodes, setPromoCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInactive, setShowInactive] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCode, setEditingCode] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [copiedCode, setCopiedCode] = useState(null);
  
  // Photographer's event and request types
  const [eventTypes, setEventTypes] = useState([]);
  const [requestTypes, setRequestTypes] = useState([]);
  
  // Feature gating
  const { hasFeature } = useFeatureGate();
  const hasPromoCodes = hasFeature("sell_downloads");
  
  // Form state
  const [formData, setFormData] = useState({
    code: "",
    description: "",
    discount_type: "percentage",
    discount_value: "",
    max_uses: "",
    max_uses_per_customer: "",
    starts_at: "",
    expires_at: "",
    applies_to_event_types: [],
    applies_to_request_types: [],
    min_order_amount: "",
    is_active: true
  });

  useEffect(() => {
    // Only fetch if user has promo codes feature
    if (hasPromoCodes) {
      fetchPromoCodes();
      fetchPhotographerConfig();
    } else {
      setLoading(false);
    }
  }, [showInactive, hasPromoCodes]);

  const fetchPromoCodes = async () => {
    try {
      const response = await axios.get(
        `${API}/promo-codes?include_inactive=${showInactive}`,
        { withCredentials: true }
      );
      setPromoCodes(response.data);
    } catch (error) {
      toast.error("Failed to load promo codes");
    } finally {
      setLoading(false);
    }
  };

  const fetchPhotographerConfig = async () => {
    try {
      const response = await axios.get(`${API}/photographer/profile`, { withCredentials: true });
      const config = response.data?.booking_config || {};
      setEventTypes(config.event_types || ["Wedding", "Portrait", "Event", "Commercial", "Other"]);
      setRequestTypes(config.request_types || ["Full Day", "Half Day", "Hourly", "Package"]);
    } catch (error) {
      // Use defaults
      setEventTypes(["Wedding", "Portrait", "Event", "Commercial", "Other"]);
      setRequestTypes(["Full Day", "Half Day", "Hourly", "Package"]);
    }
  };

  const resetForm = () => {
    setFormData({
      code: "",
      description: "",
      discount_type: "percentage",
      discount_value: "",
      max_uses: "",
      max_uses_per_customer: "",
      starts_at: "",
      expires_at: "",
      applies_to_event_types: [],
      applies_to_request_types: [],
      min_order_amount: "",
      is_active: true
    });
    setEditingCode(null);
  };

  const openCreateDialog = () => {
    resetForm();
    setDialogOpen(true);
  };

  const openEditDialog = (code) => {
    setEditingCode(code);
    setFormData({
      code: code.code,
      description: code.description || "",
      discount_type: code.discount_type,
      discount_value: code.discount_value?.toString() || "",
      max_uses: code.max_uses?.toString() || "",
      max_uses_per_customer: code.max_uses_per_customer?.toString() || "",
      starts_at: code.starts_at ? code.starts_at.split("T")[0] : "",
      expires_at: code.expires_at ? code.expires_at.split("T")[0] : "",
      applies_to_event_types: code.applies_to_event_types || [],
      applies_to_request_types: code.applies_to_request_types || [],
      min_order_amount: code.min_order_amount?.toString() || "",
      is_active: code.is_active
    });
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    if (!formData.code.trim()) {
      toast.error("Please enter a promo code");
      return;
    }
    if (!formData.discount_value) {
      toast.error("Please enter a discount value");
      return;
    }

    const payload = {
      code: formData.code.toUpperCase(),
      description: formData.description || null,
      discount_type: formData.discount_type,
      discount_value: parseFloat(formData.discount_value),
      max_uses: formData.max_uses ? parseInt(formData.max_uses) : null,
      max_uses_per_customer: formData.max_uses_per_customer ? parseInt(formData.max_uses_per_customer) : null,
      starts_at: formData.starts_at ? `${formData.starts_at}T00:00:00Z` : null,
      expires_at: formData.expires_at ? `${formData.expires_at}T23:59:59Z` : null,
      applies_to_event_types: formData.applies_to_event_types.length > 0 ? formData.applies_to_event_types : null,
      applies_to_request_types: formData.applies_to_request_types.length > 0 ? formData.applies_to_request_types : null,
      min_order_amount: formData.min_order_amount ? parseFloat(formData.min_order_amount) : null,
      is_active: formData.is_active
    };

    try {
      if (editingCode) {
        await axios.put(`${API}/promo-codes/${editingCode.code_id}`, payload, { withCredentials: true });
        toast.success("Promo code updated");
      } else {
        await axios.post(`${API}/promo-codes`, payload, { withCredentials: true });
        toast.success("Promo code created");
      }
      setDialogOpen(false);
      resetForm();
      fetchPromoCodes();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save promo code");
    }
  };

  const handleToggle = async (code) => {
    try {
      const response = await axios.post(
        `${API}/promo-codes/${code.code_id}/toggle`,
        {},
        { withCredentials: true }
      );
      toast.success(response.data.message);
      fetchPromoCodes();
    } catch (error) {
      toast.error("Failed to toggle promo code");
    }
  };

  const handleDelete = async (code) => {
    try {
      await axios.delete(`${API}/promo-codes/${code.code_id}`, { withCredentials: true });
      toast.success("Promo code deleted");
      setDeleteConfirm(null);
      fetchPromoCodes();
    } catch (error) {
      toast.error("Failed to delete promo code");
    }
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(code);
    setTimeout(() => setCopiedCode(null), 2000);
    toast.success("Code copied to clipboard");
  };

  const toggleEventType = (type) => {
    setFormData(prev => ({
      ...prev,
      applies_to_event_types: prev.applies_to_event_types.includes(type)
        ? prev.applies_to_event_types.filter(t => t !== type)
        : [...prev.applies_to_event_types, type]
    }));
  };

  const toggleRequestType = (type) => {
    setFormData(prev => ({
      ...prev,
      applies_to_request_types: prev.applies_to_request_types.includes(type)
        ? prev.applies_to_request_types.filter(t => t !== type)
        : [...prev.applies_to_request_types, type]
    }));
  };

  const filteredCodes = promoCodes.filter(code =>
    code.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
    code.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getStatusBadge = (code) => {
    if (!code.is_active) {
      return <Badge variant="outline" className="bg-gray-500/20 text-gray-400">Inactive</Badge>;
    }
    if (code.is_expired) {
      return <Badge variant="outline" className="bg-red-500/20 text-red-400">Expired</Badge>;
    }
    if (code.max_uses && code.use_count >= code.max_uses) {
      return <Badge variant="outline" className="bg-yellow-500/20 text-yellow-400">Limit Reached</Badge>;
    }
    return <Badge variant="outline" className="bg-green-500/20 text-green-400">Active</Badge>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  // Feature gating - show locked state for Starter users
  if (!hasPromoCodes) {
    return (
      <div className="space-y-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl md:text-4xl tracking-tight mb-2">Promo Codes</h1>
            <p className="text-muted-foreground">Create and manage discount codes for your clients</p>
          </div>
        </div>
        <FeatureGate feature="sell_downloads" fallback="Upgrade to Pro to create promo codes for your clients">
          {/* Placeholder content shown in locked state */}
          <Card className="h-[300px]">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Tag className="w-5 h-5" />
                Your Promo Codes
              </CardTitle>
              <CardDescription>
                Create discount codes to offer your clients special pricing
              </CardDescription>
            </CardHeader>
          </Card>
        </FeatureGate>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl md:text-4xl tracking-tight mb-2">Promo Codes</h1>
          <p className="text-muted-foreground">Create and manage discount codes for your clients</p>
        </div>
        <Button onClick={openCreateDialog} className="btn-primary" data-testid="create-promo-code-btn">
          <Plus className="w-4 h-4 mr-2" />
          Create Code
        </Button>
      </div>

      {/* Filters */}
      <Card className="bg-card border-border">
        <CardContent className="py-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search codes..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-promo-codes"
              />
            </div>
            <div className="flex items-center gap-2">
              <Switch
                checked={showInactive}
                onCheckedChange={setShowInactive}
                id="show-inactive"
              />
              <Label htmlFor="show-inactive" className="text-sm">Show inactive</Label>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Stats */}
      <div className="grid gap-4 sm:grid-cols-3">
        <Card className="bg-card border-border">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Active Codes</p>
                <p className="text-2xl font-serif">{promoCodes.filter(c => c.is_active && !c.is_expired).length}</p>
              </div>
              <Tag className="w-8 h-8 text-primary opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-card border-border">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Uses</p>
                <p className="text-2xl font-serif">{promoCodes.reduce((acc, c) => acc + (c.use_count || 0), 0)}</p>
              </div>
              <Users className="w-8 h-8 text-primary opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-card border-border">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Discounts</p>
                <p className="text-2xl font-serif">${promoCodes.reduce((acc, c) => acc + (c.total_discount_given || 0), 0).toFixed(2)}</p>
              </div>
              <DollarSign className="w-8 h-8 text-primary opacity-50" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Promo Codes List */}
      {filteredCodes.length === 0 ? (
        <Card className="bg-card border-border">
          <CardContent className="py-12 text-center">
            <Tag className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-30" />
            <p className="text-muted-foreground">No promo codes found</p>
            <Button onClick={openCreateDialog} variant="outline" className="mt-4">
              Create your first code
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {filteredCodes.map((code, index) => (
            <motion.div
              key={code.code_id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
            >
              <Card className={`bg-card border-border ${!code.is_active ? 'opacity-60' : ''}`}>
                <CardContent className="py-4">
                  <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                    {/* Code & Discount */}
                    <div className="flex items-center gap-4">
                      <div className="p-3 bg-primary/10 rounded-lg">
                        {code.discount_type === "percentage" ? (
                          <Percent className="w-6 h-6 text-primary" />
                        ) : (
                          <DollarSign className="w-6 h-6 text-primary" />
                        )}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => copyCode(code.code)}
                            className="font-mono text-lg font-bold hover:text-primary transition-colors flex items-center gap-1"
                          >
                            {code.code}
                            {copiedCode === code.code ? (
                              <Check className="w-4 h-4 text-green-500" />
                            ) : (
                              <Copy className="w-4 h-4 opacity-50" />
                            )}
                          </button>
                          {getStatusBadge(code)}
                        </div>
                        <p className="text-sm text-muted-foreground">
                          {code.discount_type === "percentage"
                            ? `${code.discount_value}% off`
                            : `$${code.discount_value} off`}
                          {code.description && ` • ${code.description}`}
                        </p>
                      </div>
                    </div>

                    {/* Stats */}
                    <div className="flex items-center gap-6 text-sm">
                      <div className="text-center">
                        <p className="text-muted-foreground">Uses</p>
                        <p className="font-medium">
                          {code.use_count || 0}
                          {code.max_uses && <span className="text-muted-foreground">/{code.max_uses}</span>}
                        </p>
                      </div>
                      {code.expires_at && (
                        <div className="text-center">
                          <p className="text-muted-foreground">Expires</p>
                          <p className="font-medium">{new Date(code.expires_at).toLocaleDateString()}</p>
                        </div>
                      )}
                      <div className="text-center">
                        <p className="text-muted-foreground">Saved</p>
                        <p className="font-medium text-green-500">${(code.total_discount_given || 0).toFixed(2)}</p>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleToggle(code)}
                        title={code.is_active ? "Deactivate" : "Activate"}
                        data-testid={`toggle-promo-${code.code}`}
                      >
                        {code.is_active ? (
                          <ToggleRight className="w-5 h-5 text-green-500" />
                        ) : (
                          <ToggleLeft className="w-5 h-5 text-muted-foreground" />
                        )}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openEditDialog(code)}
                        data-testid={`edit-promo-${code.code}`}
                      >
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setDeleteConfirm(code)}
                        className="text-red-500 hover:text-red-600"
                        data-testid={`delete-promo-${code.code}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>

                  {/* Restrictions */}
                  {(code.applies_to_event_types?.length > 0 || code.applies_to_request_types?.length > 0 || code.min_order_amount) && (
                    <div className="mt-3 pt-3 border-t border-border flex flex-wrap gap-2">
                      {code.applies_to_event_types?.map(type => (
                        <Badge key={type} variant="outline" className="text-xs">{type}</Badge>
                      ))}
                      {code.applies_to_request_types?.map(type => (
                        <Badge key={type} variant="outline" className="text-xs">{type}</Badge>
                      ))}
                      {code.min_order_amount && (
                        <Badge variant="outline" className="text-xs">Min ${code.min_order_amount}</Badge>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingCode ? "Edit Promo Code" : "Create Promo Code"}</DialogTitle>
            <DialogDescription>
              {editingCode ? "Update the promo code details" : "Create a new discount code for your clients"}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Code & Description */}
            <div className="grid gap-4">
              <div>
                <Label htmlFor="code">Code *</Label>
                <Input
                  id="code"
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                  placeholder="SUMMER20"
                  className="font-mono"
                  disabled={!!editingCode}
                  data-testid="promo-code-input"
                />
              </div>
              <div>
                <Label htmlFor="description">Description (internal)</Label>
                <Input
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Summer 2024 promotion"
                />
              </div>
            </div>

            {/* Discount */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Discount Type *</Label>
                <Select
                  value={formData.discount_type}
                  onValueChange={(v) => setFormData({ ...formData, discount_type: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percentage">Percentage (%)</SelectItem>
                    <SelectItem value="fixed">Fixed Amount ($)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="discount_value">
                  {formData.discount_type === "percentage" ? "Percentage *" : "Amount *"}
                </Label>
                <Input
                  id="discount_value"
                  type="number"
                  value={formData.discount_value}
                  onChange={(e) => setFormData({ ...formData, discount_value: e.target.value })}
                  placeholder={formData.discount_type === "percentage" ? "20" : "50"}
                  min="0"
                  max={formData.discount_type === "percentage" ? "100" : undefined}
                  data-testid="discount-value-input"
                />
              </div>
            </div>

            {/* Usage Limits */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="max_uses">Max Total Uses</Label>
                <Input
                  id="max_uses"
                  type="number"
                  value={formData.max_uses}
                  onChange={(e) => setFormData({ ...formData, max_uses: e.target.value })}
                  placeholder="Unlimited"
                  min="1"
                />
              </div>
              <div>
                <Label htmlFor="max_uses_per_customer">Max Per Customer</Label>
                <Input
                  id="max_uses_per_customer"
                  type="number"
                  value={formData.max_uses_per_customer}
                  onChange={(e) => setFormData({ ...formData, max_uses_per_customer: e.target.value })}
                  placeholder="Unlimited"
                  min="1"
                />
              </div>
            </div>

            {/* Validity Period */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="starts_at">Start Date</Label>
                <Input
                  id="starts_at"
                  type="date"
                  value={formData.starts_at}
                  onChange={(e) => setFormData({ ...formData, starts_at: e.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="expires_at">Expiry Date</Label>
                <Input
                  id="expires_at"
                  type="date"
                  value={formData.expires_at}
                  onChange={(e) => setFormData({ ...formData, expires_at: e.target.value })}
                />
              </div>
            </div>

            {/* Minimum Order */}
            <div>
              <Label htmlFor="min_order_amount">Minimum Order Amount ($)</Label>
              <Input
                id="min_order_amount"
                type="number"
                value={formData.min_order_amount}
                onChange={(e) => setFormData({ ...formData, min_order_amount: e.target.value })}
                placeholder="No minimum"
                min="0"
              />
            </div>

            {/* Event Types */}
            <div>
              <Label className="mb-2 block">Applies to Event Types</Label>
              <p className="text-xs text-muted-foreground mb-2">Leave empty to apply to all events</p>
              <div className="flex flex-wrap gap-2">
                {eventTypes.map(type => (
                  <button
                    key={type}
                    type="button"
                    onClick={() => toggleEventType(type)}
                    className={`px-3 py-1 rounded-full text-sm border transition-colors ${
                      formData.applies_to_event_types.includes(type)
                        ? "bg-primary text-primary-foreground border-primary"
                        : "border-border hover:border-primary"
                    }`}
                  >
                    {type}
                  </button>
                ))}
              </div>
            </div>

            {/* Request Types */}
            <div>
              <Label className="mb-2 block">Applies to Request Types</Label>
              <p className="text-xs text-muted-foreground mb-2">Leave empty to apply to all request types</p>
              <div className="flex flex-wrap gap-2">
                {requestTypes.map(type => (
                  <button
                    key={type}
                    type="button"
                    onClick={() => toggleRequestType(type)}
                    className={`px-3 py-1 rounded-full text-sm border transition-colors ${
                      formData.applies_to_request_types.includes(type)
                        ? "bg-primary text-primary-foreground border-primary"
                        : "border-border hover:border-primary"
                    }`}
                  >
                    {type}
                  </button>
                ))}
              </div>
            </div>

            {/* Active Toggle */}
            <div className="flex items-center justify-between">
              <div>
                <Label>Active Status</Label>
                <p className="text-xs text-muted-foreground">Enable or disable this code</p>
              </div>
              <Switch
                checked={formData.is_active}
                onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} data-testid="cancel-promo-code-btn">Cancel</Button>
            <Button onClick={handleSubmit} className="btn-primary" data-testid="submit-promo-code-btn">
              {editingCode ? "Update" : "Create"} Code
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Promo Code</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete the code <strong>{deleteConfirm?.code}</strong>? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirm(null)} data-testid="cancel-delete-btn">Cancel</Button>
            <Button variant="destructive" onClick={() => handleDelete(deleteConfirm)} data-testid="confirm-delete-btn">Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PromoCodesPage;
